import React from 'react'
import * as moment from 'moment'
import { DayPickerRangeController, DayPickerSingleDateController } from 'react-dates'

import * as constants from '../../static/constants'
import styled from 'styled-components'
import { pushDataLayer } from 'utils/googleTagManager'
import DateRangePickerCustomStyle from 'static/dateRangePickerCustomStyle'
import { Button } from 'components/atoms'
import { ISelectedDate } from 'utils/reservation'

interface IMultiDatePickerProps {
  forSingleDate?: boolean
  noShadow?: boolean
  selectedModalDate: moment.Moment | null
  dates: moment.Moment[]
  autoApply?: boolean
  setDates: React.Dispatch<React.SetStateAction<moment.Moment[]>>
  setIsOpenDatePicker?: React.Dispatch<React.SetStateAction<boolean>>
}

export const MultiDatePicker: React.VFC<IMultiDatePickerProps> = ({
  forSingleDate = false,
  noShadow = false,
  autoApply = false,
  selectedModalDate,
  dates,
  setDates,
  setIsOpenDatePicker,
}) => {
  const [selectedDates, setSelectedDates] = React.useState<ISelectedDate>({
    startDate: dates.length > 0 ? dates[0] : null,
    endDate: dates.length > 1 ? dates[dates.length - 1] : dates.length > 0 ? dates[0] : null,
  })

  const [focusedInput, setFocusedInput] = React.useState<null | 'startDate' | 'endDate'>(
    'startDate'
  )

  const onDatesChange = ({ startDate, endDate }: ISelectedDate) => {
    setSelectedDates({
      startDate,
      endDate: focusedInput === 'startDate' ? startDate : endDate,
    })
  }

  const applyDates = React.useCallback(
    (autoClose = true) => {
      if (selectedDates.startDate && selectedDates.endDate) {
        const diff = selectedDates.endDate.diff(selectedDates.startDate, 'days')
        const newDates = []
        if (diff === 0) {
          newDates.push(selectedDates.startDate)
        } else {
          for (let i = 0; i <= diff; i++) {
            const newDate = moment(selectedDates.startDate).add(i, 'days')
            newDates.push(newDate)
          }
        }
        setDates(newDates)
        pushDataLayer({
          event: 'click_tour_date',
          tour_date: selectedDates.startDate.format('YYYY/MM/DD'),
        })
        pushDataLayer({
          event: 'click_tour_date',
          tour_date: selectedDates.endDate.format('YYYY/MM/DD'),
        })
      } else if (!selectedDates.startDate && !selectedDates.endDate) {
        setDates([])
      }
      setFocusedInput('startDate')

      if (autoClose && setIsOpenDatePicker) {
        setIsOpenDatePicker(false)
      }
    },
    [selectedDates.startDate, selectedDates.endDate]
  )

  React.useEffect(() => {
    if (autoApply) {
      applyDates(false)
    }
  }, [autoApply, selectedDates.startDate, selectedDates.endDate])

  const onDateRangePickerMonthChange = async (changedMoment: moment.Moment) => {
    onDatesChange({ startDate: null, endDate: null })
    setDates([])
  }

  const handleCalendarOutSideRange = React.useCallback(calendarMoment => {
    if (calendarMoment.isBefore(moment())) {
      return true
    }
  }, [])

  const isMobile = matchMedia(`(max-width: ${constants.BREAKPOINT_TABLET_LARGE}px)`).matches
  return (
    <MultiDatePickerWrapper className={`${noShadow ? 'noShadow' : ''} ${isMobile ? 'mobile' : ''}`}>
      <DateRangePickerCustomStyle />
      {forSingleDate ? (
        <DayPickerSingleDateController
          date={selectedDates.startDate}
          onDateChange={(date: moment.Moment | null) => {
            setSelectedDates({ startDate: date, endDate: date })
          }}
          focused={focusedInput}
          initialVisibleMonth={() => selectedModalDate}
          numberOfMonths={isMobile ? 1 : 2}
          hideKeyboardShortcutsPanel={true}
          noBorder={true}
          enableOutsideDays={false}
          isOutsideRange={handleCalendarOutSideRange}
        />
      ) : (
        <DayPickerRangeController
          minimumNights={0}
          noBorder={true}
          startDate={selectedDates.startDate}
          endDate={selectedDates.endDate}
          onDatesChange={onDatesChange}
          initialVisibleMonth={() => selectedModalDate}
          enableOutsideDays={false}
          numberOfMonths={isMobile ? 1 : 2}
          hideKeyboardShortcutsPanel={true}
          onPrevMonthClick={onDateRangePickerMonthChange}
          onNextMonthClick={onDateRangePickerMonthChange}
          focusedInput={focusedInput}
          onFocusChange={(currentFocusedInput: null | 'startDate' | 'endDate') =>
            setFocusedInput(currentFocusedInput || 'startDate')
          }
          isOutsideRange={handleCalendarOutSideRange}
        />
      )}

      <div className="Buttons DatepickerButtons">
        <Button
          handleClick={() => {
            // 内部状態と親の状態の両方をリセット
            onDatesChange({ startDate: null, endDate: null })
            setDates([])
            if (setIsOpenDatePicker) {
              setIsOpenDatePicker(false)
            }
          }}
          large={true}
          textColor={constants.COLORS.subText}
          borderColor={constants.COLORS.divider}
        >
          Reset
        </Button>
        {!autoApply && (
          <Button primary={true} handleClick={applyDates} large={true}>
            Save
          </Button>
        )}
      </div>
    </MultiDatePickerWrapper>
  )
}

const MultiDatePickerWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;

  width: max-content;
  background-color: ${constants.COLORS.white};
  padding: 0.8rem;
  border-radius: 1.2rem;
  box-shadow: ${constants.COLORS.cardShadow};

  @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
    margin-left: 1.6rem;
  }
  @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
    margin-left: 0;
    > div {
      margin: 0 auto;
    }
  }
  &.noShadow {
    box-shadow: none;
    padding: 0;
  }
  &.mobile {
    width: 100%;
    margin: 0 auto;
  }
  .DatepickerButtons {
    flex: none;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 0.4rem 2rem 1.6rem 0;

    button {
      width: 11rem;
      & + button {
        margin-left: 1.6rem;
      }
    }
  }
`
